import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Eye, ExternalLink, Copy, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal that previews an actual sent email:
 * - If `campaignId` is given, fetches /api/email/campaigns/{id} and shows subject + body_html in an iframe.
 * - If `subject` + `bodyHtml` are given directly (e.g. draft preview), skips the fetch.
 */
export const EmailPreviewModal = ({ campaignId, subject: subjectProp, bodyHtml: bodyHtmlProp, onClose }) => {
  const [loading, setLoading] = useState(!!campaignId);
  const [data, setData] = useState(campaignId ? null : { subject: subjectProp, body_html: bodyHtmlProp });

  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    axios.get(`${API}/email/campaigns/${campaignId}`)
      .then((r) => setData(r.data?.campaign || null))
      .catch(() => toast.error('Failed to load email preview'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  const copySubject = () => {
    if (!data?.subject) return;
    navigator.clipboard.writeText(data.subject).then(
      () => toast.success('Subject copied'),
      () => toast.error('Copy failed'),
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="email-preview-modal">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2 min-w-0">
            <Eye className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <h3 className="font-black text-sm uppercase tracking-wider text-zinc-900">Email preview</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-2xl leading-none" data-testid="email-preview-close" aria-label="Close">×</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>
        ) : !data ? (
          <div className="p-8 text-center text-sm text-zinc-500">Email not available.</div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-zinc-200 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400">Subject</div>
                  <div className="text-sm font-bold text-zinc-900 truncate" data-testid="email-preview-subject">{data.subject || '—'}</div>
                </div>
                <button
                  onClick={copySubject}
                  className="flex-shrink-0 flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-400 px-2 py-1"
                  data-testid="email-preview-copy-subject"
                  title="Copy subject"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <div className="flex items-center gap-4 flex-wrap text-[11px] text-zinc-500">
                {data.email_type && <span><strong className="text-zinc-700">Type:</strong> {data.email_type.replace(/_/g, ' ')}</span>}
                {data.variant_label && <span><strong className="text-zinc-700">Variant:</strong> {data.variant_label}</span>}
                {data.total_recipients !== undefined && <span><strong className="text-zinc-700">Recipients:</strong> {data.total_recipients}</span>}
                {data.created_at && <span>{new Date(data.created_at).toLocaleString()}</span>}
              </div>
            </div>

            <div className="flex-1 overflow-hidden bg-zinc-100 p-4">
              {data.body_html ? (
                <iframe
                  title="Email preview"
                  srcDoc={data.body_html}
                  sandbox=""
                  className="w-full h-full bg-white border border-zinc-300"
                  data-testid="email-preview-iframe"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-center px-8">
                  <div>
                    <p className="text-sm text-zinc-700 font-bold mb-2">HTML body wasn&apos;t captured for this campaign.</p>
                    <p className="text-xs text-zinc-500">Campaigns sent before Feb 25, 2026 don&apos;t include the rendered HTML. New sends will show a full preview.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <span className="text-[11px] text-zinc-500">Campaign ID: <code className="bg-white px-1 py-0.5 border border-zinc-200">{data.id || campaignId || '—'}</code></span>
              {data.body_html && (
                <button
                  onClick={() => {
                    const w = window.open('', '_blank');
                    if (w) { w.document.write(data.body_html); w.document.close(); }
                  }}
                  className="flex items-center gap-1 text-[11px] font-bold text-zinc-600 hover:text-zinc-900"
                  data-testid="email-preview-newtab"
                >
                  <ExternalLink className="w-3 h-3" /> Open in new tab
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
